import {
    CreateParams,
    CreateResult,
    DataProvider,
    DeleteParams,
    DeleteResult,
    GetListParams,
    GetListResult,
    GetManyParams,
    GetManyResult,
    GetOneParams,
    GetOneResult,
    HttpError,
    UpdateParams,
    UpdateResult,
} from 'react-admin';
import { read_cookie } from 'sfcookies';
import { apiUrl, UseUrlManagerCleanUrl } from '../components/utils/url-manager';
import { addSortToUrl, processFilters } from './provider/defaults';
import { AdminRole } from '../types/entities';

/**
 * The ACL roles resource.
 *
 * Modelled on nokProcessingFailureDataProvider and salesConversationDataProvider rather than the
 * 400-line template, for the reason recorded against HQ-06: those copies swallow a non-2xx and
 * hand react-admin an empty result. That is wrong on any screen and unusable on this one, where
 * every refusal is a sentence the admin needs to read. HQ-API answers "you cannot grant what you
 * do not hold" and "the Owner role is part of the application"; both have to reach the notification
 * rather than becoming an empty list or a silent no-op.
 */

const headers = (): Headers => new Headers({
    'Content-Type': 'application/ld+json',
    'token': read_cookie('auth'),
    'hq': 'hq',
});

const patchHeaders = (): Headers => new Headers({
    'Content-Type': 'application/merge-patch+json',
    'token': read_cookie('auth'),
    'hq': 'hq',
});

/**
 * Read the body once, then decide.
 *
 * A second `.json()` on a consumed body throws, and the real error is lost behind it, so the
 * parse happens before the status is branched on rather than inside each branch.
 */
const readOrThrow = async (response: Response, fallback: string): Promise<any> => {
    let body: any = null;
    try {
        body = await response.json();
    } catch {
        body = null;
    }

    if (!response.ok) {
        const message: string = typeof body?.['hydra:description'] === 'string' && body['hydra:description'].length > 0
            ? body['hydra:description']
            : fallback;
        throw new HttpError(message, response.status, body);
    }

    return body;
};

const fetchCollection = async (url: string): Promise<{ data: AdminRole[], total: number }> => {
    const request: Request = new Request(UseUrlManagerCleanUrl({ url }), { method: 'GET', headers: headers() });
    const body: any = await readOrThrow(await fetch(request), 'Could not load the admin roles');

    return {
        data: body?.['hydra:member'] ?? body?.member ?? [],
        total: body?.['hydra:totalItems'] ?? body?.totalItems ?? 0,
    };
};

/**
 * What HQ-API accepts. `resources` is a flat list of ACL ids rather than rule objects, and the
 * server turns it into rows; `isSystem` is read-only there, so sending it back would be noise.
 */
const writeable = (data: any): Record<string, unknown> => {
    const payload: Record<string, unknown> = {
        name: data?.name,
        description: data?.description ?? null,
        isAll: data?.isAll === true,
    };

    // Left out entirely when the form did not touch them, which is how HQ-API tells "grant
    // nothing" from "leave the grants alone".
    if (Array.isArray(data?.resources)) {
        payload.resources = data.resources;
    }

    return payload;
};

const adminRoleDataProvider: DataProvider = {
    getList: async (resource: string, params: GetListParams): Promise<GetListResult> => {
        let url: string = apiUrl + resource;
        if (params.pagination) {
            url += `?page=${ params.pagination.page }&itemsPerPage=${ params.pagination.perPage }`;
        }
        url = processFilters(url, params);
        url = addSortToUrl(url, params);

        return fetchCollection(url);
    },

    getOne: async (resource: string, params: GetOneParams): Promise<GetOneResult> => {
        const url: string = UseUrlManagerCleanUrl({ url: `${ apiUrl }${ resource }/${ params.id }` });
        const response: Response = await fetch(new Request(url, { method: 'GET', headers: headers() }));

        return { data: await readOrThrow(response, 'Could not load the admin role') };
    },

    getMany: async (resource: string, params: GetManyParams): Promise<GetManyResult> => {
        if (params.ids.length === 0) {
            return { data: [] };
        }
        const query: string = params.ids.map((id): string => `id[]=${ String(id) }`).join('&');
        const { data } = await fetchCollection(`${ apiUrl }${ resource }?${ query }`);

        return { data };
    },

    create: async (resource: string, params: CreateParams): Promise<CreateResult> => {
        const url: string = UseUrlManagerCleanUrl({ url: apiUrl + resource });
        const response: Response = await fetch(new Request(url, {
            method: 'POST',
            headers: headers(),
            body: JSON.stringify(writeable(params.data)),
        }));

        return { data: await readOrThrow(response, 'Could not create the admin role') };
    },

    update: async (resource: string, params: UpdateParams): Promise<UpdateResult> => {
        const url: string = UseUrlManagerCleanUrl({ url: `${ apiUrl }${ resource }/${ params.id }` });
        const response: Response = await fetch(new Request(url, {
            method: 'PATCH',
            headers: patchHeaders(),
            body: JSON.stringify(writeable(params.data)),
        }));

        return { data: await readOrThrow(response, 'Could not save the admin role') };
    },

    delete: async (resource: string, params: DeleteParams): Promise<DeleteResult> => {
        const url: string = UseUrlManagerCleanUrl({ url: `${ apiUrl }${ resource }/${ params.id }` });
        const response: Response = await fetch(new Request(url, { method: 'DELETE', headers: headers() }));
        if (!response.ok) {
            // A refused delete is the interesting case here: a system role, or one somebody still
            // holds. HQ-API says which in hydra:description and the admin has to see it.
            await readOrThrow(response, 'Could not delete the admin role');
        }

        return { data: params.previousData ?? { id: params.id } } as DeleteResult;
    },

    getManyReference: (): Promise<never> => Promise.reject(new Error('Not supported')),
    updateMany: (): Promise<never> => Promise.reject(new Error('Not supported')),
    // Deliberately absent. Deleting roles in bulk is how somebody removes the one they are
    // standing on; each deletion is refused or allowed on its own merits.
    deleteMany: (): Promise<never> => Promise.reject(new Error('Roles are deleted one at a time')),
};

export default adminRoleDataProvider;
